import React, { useCallback } from 'react';
import { useContext } from 'react';
import * as http from 'utils/http';
import config from 'config';
import useSWR, { mutate as swrMutate } from 'swr';

import { getBusinessFilter } from 'utils/filters';

import moment from 'moment';
import serialize from 'utils/serialize';
import { LH_EXPRESS_ROUTE_FOR_FILTER, LH_EXPRESS_FR_ROUTE_FOR_FILTER } from 'utils/lhexpress';
import { hasInternationalRouteSelected } from 'utils/international';

const GatekeepingServiceContext = React.createContext();

const fetcher = (url) => http.get(`${config.API_URL}${url}`);

const _gateKeepingServiceURL = (baseUrl, filters) => {
  if (!filters) {
    return baseUrl;
  }

  const { route, vehicle_type } = filters;

  const isInternational = hasInternationalRouteSelected(route);
  const isLhEs = (route || []).some((r) => r.value === LH_EXPRESS_ROUTE_FOR_FILTER.name);
  const isLhFr = (route || []).some((r) => r.value === LH_EXPRESS_FR_ROUTE_FOR_FILTER.name);
  const regularRoutes = (route || []).filter(
    (r) =>
      ![LH_EXPRESS_ROUTE_FOR_FILTER.name, LH_EXPRESS_FR_ROUTE_FOR_FILTER.name].includes(r.value)
  );

  const routeFilter = isInternational
    ? undefined
    : regularRoutes.map((route) => `${route.value}->${route.value}`).join(',');

  const suitableVehicleTypeFilter = (vehicle_type || [])
    .map((vehicleType) => vehicleType.value)
    .join(',');

  return `${baseUrl}?${serialize({
    page_size: 0,
    date_from: filters?.date?.from,
    date_to: filters?.date?.to,
    route: routeFilter || undefined,
    suitable_vehicle_type: suitableVehicleTypeFilter || undefined,
    shipper: filters?.shipper?.value,
    international: isInternational || undefined,
    long_haul: isLhEs || undefined,
    long_haul_fr: isLhFr || undefined,
    ...getBusinessFilter(filters?.business, 'is_ftl'),
  })}`;
};

const _groupShipperOrdersByDate = (shipperOrders) => {
  return shipperOrders.reduce((acc, shipperOrder) => {
    const { start_time_from } = shipperOrder;
    const date = moment(start_time_from).format('YYYY-MM-DD');
    return {
      ...acc,
      [date]: [...(acc[date] || []), shipperOrder],
    };
  }, {});
};

const _useGatekeepingData = (filters, swrOptions) => {
  const url = _gateKeepingServiceURL('gatekeeper/', filters);

  const { data: shipperOrders, mutate } = useSWR(url, fetcher, swrOptions);

  const groupedShipperOrders = _groupShipperOrdersByDate(shipperOrders || []);

  return { groupedShipperOrders, mutate };
};

const _useGatekeepingCounter = (filters, swrOptions) => {
  const url = _gateKeepingServiceURL('gatekeeper/count/', filters);

  const { data: count } = useSWR(url, fetcher, swrOptions);

  const countData = count;
  const countTotal = Object.keys(count || {}).reduce((acc, date) => acc + countData[date], 0);

  return { countData, countTotal };
};

const useGatekeepingDetail = (id) => {
  const { data: detail } = useSWR(!!id && `mercury/shipper_orders/${id}/`, fetcher);

  return { detail };
};

const useGatekeepingStops = (id, swrOptions) => {
  const { data: stops } = useSWR(!!id && `gatekeeper/${id}/stops/`, fetcher, swrOptions);

  const hasComments = stops?.some(({ comments }) => !!comments);
  const hasCustomLoads = stops?.some(({ loads }) => {
    return loads.some(({ pallet_type }) => pallet_type === 'CUSTOM_PALLET');
  });

  return { stops, hasComments, hasCustomLoads };
};

const useGatekeepingDataFiltered = (swrOptions) => {
  const { filters } = useContext(GatekeepingServiceContext);

  return _useGatekeepingData(filters, swrOptions);
};

const useGatekeepingCounterFiltered = (swrOptions) => {
  const { filters } = useContext(GatekeepingServiceContext);
  return _useGatekeepingCounter(filters, swrOptions);
};

const useGatekeepingDataMutate = () => {
  const { filters } = useContext(GatekeepingServiceContext);

  const mutate = useCallback(() => {
    const urls = [_gateKeepingServiceURL('gatekeeper/', filters)];
    urls.forEach((url) => swrMutate(url));
  }, [filters]);

  return { mutate };
};

const reviewShipperOrder = (id) => {
  return http.patch(`${config.API_URL}gatekeeper/${id}/review/`);
};

export {
  // Private functions
  _useGatekeepingData,
  _useGatekeepingCounter,
  _gateKeepingServiceURL,
  _groupShipperOrdersByDate,
  // hooks
  useGatekeepingDataFiltered,
  useGatekeepingCounterFiltered,
  useGatekeepingDetail,
  useGatekeepingStops,
  useGatekeepingDataMutate,
  reviewShipperOrder,
  // Context
  GatekeepingServiceContext,
};
